import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from './shared';

function credColor(cred) {
  if (!cred) return '#64748b';
  if (cred.includes('🟢') || /strong/i.test(cred)) return '#10b981';
  if (cred.includes('🔴') || /weak/i.test(cred))   return '#ef4444';
  return '#f59e0b';
}

function assessColor(a) {
  if (!a) return '#64748b';
  if (/strong/i.test(a) || a.includes('🟢')) return '#10b981';
  if (/weak/i.test(a) || a.includes('🔴'))   return '#ef4444';
  return '#f59e0b';
}

function initials(name) {
  return name.split(/\s+/).map(w => w[0] || '').slice(0, 2).join('').toUpperCase();
}

// Strip Vietnamese diacritics so matching is robust to NFC/NFD encoding.
function deaccent(str) {
  return str
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd').replace(/Đ/g, 'D')
    .toLowerCase();
}

// Placeholder/non-person names that must NOT link to the personnel feature
// (matched against the de-accented, lowercased name)
const PLACEHOLDER_RE = /an danh|giau ten|dang cap nhat|unknown|anonymous|pseudonymous|undisclosed|not disclosed|to be announced|\btba\b|\bn\/?a\b/;

function isRealPerson(name) {
  if (!name) return false;
  const ascii = deaccent(name).replace(/[[\](){}]/g, '').trim();
  if (ascii.length < 2) return false;
  // A real person's name must contain letters (filters "------", "???", etc.)
  if (!/[a-z]/.test(ascii)) return false;
  // Any "chua ..." / "khong ..." phrase (chua cong khai, chua xac dinh,
  // khong ro, khong cong khai, ...) is a placeholder, not a person.
  if (/^(chua|khong)\b/.test(ascii)) return false;
  return !PLACEHOLDER_RE.test(ascii);
}

const AVATAR_COLORS = ['#3b82f6','#06b6d4','#8b5cf6','#10b981','#f59e0b','#ec4899'];

export default function TeamCard({ data, onPersonClick, projectInfo }) {
  const { t } = useTranslation();
  const ac = assessColor(data.assessment);

  return (
    <Card title={t('teamCard.title')} icon="👥">
      {data.members.length > 0 ? (
        <div style={styles.table}>
          <div style={styles.headerRow}>
            <span style={styles.th}>{t('teamCard.colMember')}</span>
            <span style={styles.th}>{t('teamCard.colRole')}</span>
            <span style={{ ...styles.th, flex: 2 }}>{t('teamCard.colBackground')}</span>
            <span style={styles.th}>{t('teamCard.colCredibility')}</span>
          </div>
          {data.members.map((m, i) => {
            const cc = credColor(m.credibility);
            return (
              <div key={i} style={styles.row}>
                <div style={styles.memberCell}>
                  <div style={{ ...styles.avatar, background: AVATAR_COLORS[i % AVATAR_COLORS.length] + '30', color: AVATAR_COLORS[i % AVATAR_COLORS.length] }}>
                    {initials(m.name)}
                  </div>
                  {onPersonClick && isRealPerson(m.name) ? (
                    <button
                      style={styles.memberNameBtn}
                      onClick={() => onPersonClick(m, projectInfo)}
                      title={t('teamCard.viewProfile')}
                    >
                      {m.name} →
                    </button>
                  ) : (
                    <span style={styles.memberName}>{m.name}</span>
                  )}
                </div>
                <span style={styles.role}>{m.role}</span>
                <span style={{ ...styles.bg, flex: 2 }}>{m.background}</span>
                <span style={{ ...styles.cred, color: cc }}>
                  <span style={{ ...styles.credDot, background: cc }} />
                  {m.credibility.replace(/🟢|🟡|🔴/g, '').trim()}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <p style={{ color: '#475569', fontSize: 13 }}>{t('teamCard.noData')}</p>
      )}

      <div style={styles.footer}>
        <div style={{ ...styles.assessBadge, color: ac, borderColor: ac + '40', background: ac + '15' }}>
          {t('teamCard.teamLabel')}: {data.assessment?.replace(/🟢|🟡|🔴/g, '').trim() || '—'}
        </div>
        {data.reason && <p style={styles.reason}>{data.reason}</p>}
        {data.flags && data.flags !== 'None detected' && (
          <div style={styles.flagBox}>
            <span style={styles.flagLabel}>⚠️ {t('teamCard.warningFlags')}</span>
            <span style={styles.flagText}>{data.flags}</span>
          </div>
        )}
      </div>
    </Card>
  );
}

const styles = {
  table: { display: 'flex', flexDirection: 'column', gap: 1, marginBottom: 14 },
  headerRow: { display: 'flex', gap: 12, padding: '6px 10px', borderBottom: '1px solid var(--border)', marginBottom: 4 },
  th: { flex: 1, fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: 0.8, textTransform: 'uppercase' },
  row: {
    display: 'flex', gap: 12, alignItems: 'flex-start',
    padding: '10px 10px', borderRadius: 8, background: 'var(--surface2)',
    border: '1px solid var(--border)', marginBottom: 4,
  },
  memberCell: { flex: 1, display: 'flex', alignItems: 'center', gap: 8 },
  avatar: {
    width: 30, height: 30, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 11, fontWeight: 700, flexShrink: 0,
  },
  memberName: { fontSize: 12, fontWeight: 600, color: 'var(--text)' },
  memberNameBtn: {
    fontSize: 12, fontWeight: 600, color: 'var(--accent)', background: 'none', border: 'none',
    cursor: 'pointer', padding: 0, textDecoration: 'none',
  },
  role: { flex: 1, fontSize: 11, color: 'var(--text-dim)' },
  bg: { fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.5 },
  cred: { flex: 1, fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 },
  credDot: { width: 7, height: 7, borderRadius: '50%', flexShrink: 0 },
  footer: { display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8, borderTop: '1px solid var(--border)', paddingTop: 12 },
  assessBadge: {
    fontSize: 12, fontWeight: 700, padding: '6px 14px',
    borderRadius: 8, border: '1px solid', display: 'inline-block', alignSelf: 'flex-start',
  },
  reason: { fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6, margin: 0 },
  flagBox: { background: 'rgba(220,38,38,0.07)', border: '1px solid rgba(220,38,38,0.2)', borderRadius: 8, padding: '10px 12px' },
  flagLabel: { fontSize: 10, color: '#dc2626', fontWeight: 700, display: 'block', marginBottom: 4 },
  flagText: { fontSize: 12, color: '#b91c1c' },
};
